import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

type IProps = Immutable<templates.OrderOfferTemplate>;

export type OrderOfferComponentProps = IProps;

export type OrderOfferImageComponentProps = Pick<
    IProps,
    "desktop_image" | "mobile_image"
>;

type TemplateConfig = Immutable<{
    OrderOffer: React.ComponentType<OrderOfferComponentProps>;
    OrderOfferImage: React.ComponentType<OrderOfferImageComponentProps>;
}>;

// Factory pattern (novel for this codebase): used because each brand's
// `OrderOffer` / `OrderOfferImage` components have site-specific SCSS and
// layout, so the lib can't ship a single shared Template like most other
// reactivated templates do. Sites call this from their own
// client/templates/OrderOfferTemplate.tsx to inject their components while
// reusing the shared branching logic. If a future shared template can be
// implemented entirely in the lib (e.g. MessageBannerTemplate), prefer
// the standard `export const Template = ...` + per-site re-export pattern
// instead of this factory.
export const createOrderOfferTemplate = (
    config: TemplateConfig,
): ((props: IProps) => React.JSX.Element | null) => {
    const Template = (props: IProps): React.JSX.Element | null => {
        const OrderOffer = config.OrderOffer;
        const OrderOfferImage = config.OrderOfferImage;

        // Branching logic: empty-string fields signal "this branch doesn't
        // apply" (parity with the original CSR behavior of omitting `data-*`
        // attrs for falsy values). The Python contract types these as
        // required `str`; an empty string here is a sentinel for "absent",
        // not a missing value.
        if (props.offer_freq && props.benefit_value) {
            return (
                <OrderOffer
                    offer_freq={props.offer_freq}
                    benefit_value={props.benefit_value}
                    desktop_image={props.desktop_image}
                    mobile_image={props.mobile_image}
                    offer_name={props.offer_name}
                />
            );
        }
        if (props.desktop_image || props.mobile_image) {
            return (
                <OrderOfferImage
                    desktop_image={props.desktop_image}
                    mobile_image={props.mobile_image}
                />
            );
        }
        return null;
    };
    return Template;
};

// Reactivated auto-imports `Template` from every template module. Sites
// with custom OrderOffer/OrderOfferImage components supply the real one
// via createOrderOfferTemplate(); sites that don't render order offers
// (e.g. cocoon) re-export this noop directly.
export const Template = (_props: IProps): null => null;
